import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { StarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function TestimonialsSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 380;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const testimonials = [{
    initials: 'MG',
    name: 'María G.',
    location: 'Ciudad de México, México — mamá de 2 hijos',
    text: 'Antes me acostaba con la cabeza llena de pendientes. Ahora sé exactamente qué hacer cada día y me siento mucho más tranquila.'
  }, {
    initials: 'LP',
    name: 'Laura P.',
    location: 'Buenos Aires, Argentina — mamá de 3 hijos',
    text: 'Llevo una semana aplicando el sistema y ya noté la diferencia. Mis mañanas son más tranquilas y mis hijos también lo sienten.'
  }, {
    initials: 'AM',
    name: 'Andrea M.',
    location: 'Bogotá, Colombia — mamá emprendedora',
    text: 'Las plantillas son increíbles. Por fin tengo mis comidas, tareas y citas organizadas en un solo lugar sin complicarme.'
  }, {
    initials: 'CR',
    name: 'Carolina R.',
    location: 'Madrid, España — mamá trabajadora',
    text: 'Pensé que era otro ebook más, pero el método 10-3-1 realmente funciona. Lo aplico en 10 minutos y el resto del día fluye mejor.'
  }];
  return <section className="py-20 bg-beige-warm md:py-32 overflow-hidden" data-id="element-244">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8" data-id="element-245">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="mb-16 text-center" data-id="element-246">
          <motion.h2 variants={itemVariants} className="text-3xl font-bold md:text-4xl text-charcoal font-playfair" data-id="element-247">
            Lo que otras mamás están diciendo
          </motion.h2>
        </motion.div>

        <div className="relative" data-id="element-248">
          {/* Left Arrow */}
          <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white shadow-md border border-sage-light/30 text-sage-dark hover:bg-sage-light/20 transition-colors -ml-2 md:-ml-4" aria-label="Anterior testimonio" data-id="element-249">
            <ChevronLeftIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-250" />
          </button>

          {/* Right Arrow */}
          <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white shadow-md border border-sage-light/30 text-sage-dark hover:bg-sage-light/20 transition-colors -mr-2 md:-mr-4" aria-label="Siguiente testimonio" data-id="element-251">
            <ChevronRightIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-252" />
          </button>

          <motion.div ref={scrollRef} initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="flex gap-6 pb-8 overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] px-6 md:px-10" data-id="element-253">
            {/* Spacer for initial padding on scroll */}
            <div className="w-4 shrink-0 md:w-8" aria-hidden="true" data-id="element-254"></div>

            {testimonials.map((testimonial, index) => <div key={index} className="snap-center shrink-0 w-[300px] md:w-[360px] bg-white rounded-2xl shadow-sm p-6 md:p-8 flex flex-col border border-sage-light/30" data-id="element-255">
                <div className="flex items-center mb-6" data-id="element-256">
                  <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-full bg-sage-light text-sage-dark font-bold text-lg" data-id="element-257">
                    {testimonial.initials}
                  </div>
                  <div className="ml-4" data-id="element-258">
                    <h4 className="font-bold text-charcoal" data-id="element-259">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-charcoal/60" data-id="element-260">
                      {testimonial.location}
                    </p>
                  </div>
                </div>

                <div className="flex mb-4 space-x-1" data-id="element-261">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 text-amber-400 fill-current" data-id="element-262" />)}
                </div>

                <p className="text-charcoal/80 italic leading-relaxed flex-1" data-id="element-263">
                  "{testimonial.text}"
                </p>
              </div>)}

            {/* Spacer for end padding on scroll */}
            <div className="w-4 shrink-0 md:w-8" aria-hidden="true" data-id="element-264"></div>
          </motion.div>
        </div>
      </div>
    </section>;
}